import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { CalendarEvent } from "@lifeat/shared/calendar/impl/CalendarEvent";
import * as DateUtil from "@lifeat/shared/util/DateUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import CloseButton from "@common/LifeAtButton/Buttons/CloseButton";
import SpecialLifeAtButton from "@common/LifeAtButton/Variants/SpecialLifeAtButton";
import CameraOnIcon from "@icons/Rooms/CamOn.svg";
import { FontSize } from "@themes/Fonts";
import { JoinCalendarEventMeetingEvent } from "../../AmplitudeCalendarEvents";
import { joinEventMeetingLink } from "../../CalendarService";

const EventWrapper = styled.div<{ show: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;

    visibility: ${(props) => (props.show ? "visible" : "hidden")};
    opacity: ${(props) => (props.show ? 1 : 0)};

    transition: all 0.3s ease-in-out;
    -webkit-transition: all 0.3s ease-in-out;
    -moz-transition: all 0.3s ease-in-out;
    -ms-transition: all 0.3s ease-in-out;
    -o-transition: all 0.3s ease-in-out;

    height: auto;
    max-height: ${(props) => (props.show ? 100 : 0)}%;
    width: 100%;

    padding-top: ${(props) => (props.show ? 16 : 0)}px;
    padding-left: ${(props) => (props.show ? 20 : 0)}px;
    padding-right: ${(props) => (props.show ? 10 : 0)}px;
    padding-bottom: ${(props) => (props.show ? 16 : 0)}px;

    border-top: ${(props) => (props.show ? `1px solid ${props.theme.borderPrimary}` : `none`)};
    border-bottom: ${(props) => (props.show ? `1px solid ${props.theme.borderPrimary}` : `none`)};
`;

const EventHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;

    width: inherit;
`;

const EventTitle = styled.span`
    font-size: ${FontSize.medium};
    width: 225px;
`;

const StyledCloseButton = styled(CloseButton)`
    .lifeat-button {
        padding: unset;
        min-height: unset;
    }
`;

const EventBody = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;

    width: inherit;
    max-height: 400px;
`;

const EventDetails = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px 0;
    max-width: 100%;
`;

const EventTime = styled.div`
    font-size: ${FontSize.normal};
    color: ${(props) => props.theme.textColorSecondary};
`;

const DescriptionBlock = styled.div`
    overflow-y: scroll;
    overflow-wrap: break-word;
    word-break: break-all;
    max-height: 200px;
    font-size: ${FontSize.normal};
    * {
        color: ${(props) => props.theme.textColorPrimary};
    }
`;

const EventConferenceDetails = styled.div``;

const JoinEventConferenceBtn = styled(SpecialLifeAtButton)`
    background-color: ${(props) => props.theme.textColorPrimary} !important;
    * {
        background-color: ${(props) => props.theme.textColorPrimary} !important;
        color: ${(props) => props.theme.backgroundPrimary} !important;
    }

    padding: 10px;

    svg {
        fill: ${(props) => props.theme.backgroundPrimary};
        stroke: ${(props) => props.theme.backgroundPrimary};
    }
`;

const JOIN_EVENT_MEETING_BTN_LABEL = "Join";

interface CalendarEventDetailViewProps {
    readonly event: CalendarEvent;
    readonly isVisible: boolean;
    readonly handleHide: () => void;
    className?: string;
}
function CalendarEventDetailView(props: CalendarEventDetailViewProps) {
    const amplitudeService = AmplitudeService.getInstance();

    const history = useHistory();

    const event = props.event;

    const dateStart = new Date(event?.timeStart);
    const dateEnd = new Date(event?.timeEnd);

    const [meetingLink, setMeetingLink] = useState(null);
    useEffect(() => {
        if (event) {
            if (event.conferenceDetails) {
                setMeetingLink(event.conferenceDetails.conferenceLink);
            } else if (event.location && event.location.includes("https://")) {
                setMeetingLink(event.location);
            } else {
                setMeetingLink(null);
            }
        }
    }, [event]);

    const multipleDates = DateUtil.formatDateStringYYYYMMDD(dateStart) !== DateUtil.formatDateStringYYYYMMDD(dateEnd);
    const dateString = `${dateStart.toLocaleString("default", { weekday: "long", month: "long", day: "numeric" })} 
                            ${
                                multipleDates
                                    ? ` - ` +
                                      dateEnd.toLocaleString("default", {
                                          weekday: "long",
                                          month: "long",
                                          day: "numeric",
                                      })
                                    : ""
                            }`;
    const timeString = `${dateStart.toLocaleTimeString("default", { hour: "numeric", minute: "numeric" })} 
                            - ${dateEnd.toLocaleTimeString("default", { hour: "numeric", minute: "numeric" })}`;

    const handleJoinMtgBtnClick = () => {
        if (meetingLink) {
            joinEventMeetingLink(meetingLink, history);
            amplitudeService.logEvent(new JoinCalendarEventMeetingEvent());
        }
    };

    return (
        <EventWrapper show={props.isVisible} className={props.className}>
            <EventHeader>
                <EventTitle>{event?.summary}</EventTitle>
                <StyledCloseButton handleClose={props.handleHide} />
            </EventHeader>

            <EventBody>
                <EventDetails>
                    <EventTime>{dateString}</EventTime>
                    <EventTime>{timeString}</EventTime>
                </EventDetails>

                {meetingLink && (
                    <EventConferenceDetails>
                        <JoinEventConferenceBtn
                            isLink={false}
                            handleClick={handleJoinMtgBtnClick}
                            text={JOIN_EVENT_MEETING_BTN_LABEL}
                            icon={<CameraOnIcon />}
                            iconFill={true}
                        />
                    </EventConferenceDetails>
                )}

                {event.description && (
                    <EventDetails>
                        {/* NOTE: this is sanitzed HTML */}
                        <DescriptionBlock dangerouslySetInnerHTML={{ __html: event?.description }} />
                    </EventDetails>
                )}
            </EventBody>
        </EventWrapper>
    );
}
export default CalendarEventDetailView;
